if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        this.initialize();
        if (!this.initialized && !this.initializationObserver) {
          this.initializationObserver = new MutationObserver(() => {
            this.initialize();
            if (this.initialized) {
              this.initializationObserver.disconnect();
              this.initializationObserver = null;
            }
          });
          this.initializationObserver.observe(this, { childList: true, subtree: true });
        }
      }

      initialize() {
        if (this.initialized) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navigation: this.querySelector('[data-gallery-navigation]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer || !this.elements.thumbnails || !this.elements.navigation) return;

        this.initialized = true;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.navigation = this.elements.navigation;
        this.navigationButtons = {
          previous: this.navigation?.querySelector('.gallery-navigation-button--previous'),
          next: this.navigation?.querySelector('.gallery-navigation-button--next'),
        };
        this.navigationButtons.previous?.addEventListener('click', (event) => this.changeMedia(event, -1));
        this.navigationButtons.next?.addEventListener('click', (event) => this.changeMedia(event, 1));
        this.refreshNavigation();
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getVisibleThumbnails() {
        if (!this.elements.thumbnails) return [];
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (thumbnail) => window.getComputedStyle(thumbnail).display !== 'none'
        );
      }

      refreshNavigation() {
        if (!this.elements.thumbnails || !this.navigation || !this.navigationButtons) return;
        const hasMultipleMedia = this.getVisibleThumbnails().length > 1;
        this.classList.toggle('has-gallery-navigation', hasMultipleMedia);
        this.navigation.hidden = !hasMultipleMedia;
        this.navigationButtons.previous?.toggleAttribute('disabled', !hasMultipleMedia);
        this.navigationButtons.next?.toggleAttribute('disabled', !hasMultipleMedia);
      }

      changeMedia(event, direction) {
        event.preventDefault();

        const thumbnails = this.getVisibleThumbnails();
        if (thumbnails.length < 2) return;

        const currentIndex = thumbnails.findIndex((thumbnail) =>
          thumbnail.querySelector('button[aria-current="true"]')
        );
        const nextIndex = (Math.max(currentIndex, 0) + direction + thumbnails.length) % thumbnails.length;
        this.setActiveMedia(thumbnails[nextIndex].dataset.target, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
